import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";

export interface AccordionItem {
  title: string;
  content: React.ReactNode;
  defaultOpen?: boolean;
  id?: string;
}

export function Accordion({ items, openSectionId, openKey }: { items: AccordionItem[]; openSectionId?: string | null; openKey?: number }) {
  const [openIndices, setOpenIndices] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    items.forEach((item, i) => {
      if (item.defaultOpen) initial.add(i);
    });
    return initial;
  });

  // Open and scroll to a section when openSectionId changes
  useEffect(() => {
    if (!openSectionId) return;
    const idx = items.findIndex((item) => item.id === openSectionId);
    if (idx === -1) return;
    setOpenIndices((prev) => {
      if (prev.has(idx)) return prev;
      return new Set(prev).add(idx);
    });
    // Scroll after the section opens (wait for CSS transition)
    setTimeout(() => {
      document.getElementById(openSectionId)?.scrollIntoView({ behavior: "smooth" });
    }, 150);
  }, [openSectionId, openKey, items]);

  const toggle = (index: number) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="divide-y divide-border border-t border-border">
      {items.map((item, i) => {
        const isOpen = openIndices.has(i);
        return (
          <div key={i} id={item.id}>
            <button
              onClick={() => toggle(i)}
              className="flex w-full items-center justify-between py-5 text-left"
            >
              <span className="text-base font-medium text-heading">
                {item.title}
              </span>
              <ChevronDown
                size={20}
                className={`flex-shrink-0 text-text-muted transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-300 ${
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
            >
              <div className="overflow-hidden">
                <div className="pb-6">{item.content}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
